import React, { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { BAND_CONTENT, DIMENSION_LABEL, DIMENSION_ORDER, dimensionLevel, strongestLayerExplanation } from './scoring';
import { decodeScoreResult } from './scoreResultUrl';
import { AuditRequestForm } from './AuditRequestForm';
import { trackScoreComplete } from '../lib/analytics';
type ScoreResultsPageProps = {
  search: string;
};
export function ScoreResultsPage({
  search
}: ScoreResultsPageProps) {
  const result = decodeScoreResult(search);
  const totalScore = result?.totalScore;
  const band = result?.band;
  useEffect(() => {
    if (totalScore === undefined || !band) return;
    trackScoreComplete(totalScore, band);
  }, [totalScore, band]);
  if (!result) {
    return <EmptyScoreState data-id="element-527" data-mp="3josk6exw8kxa" />;
  }
  const content = BAND_CONTENT[result.band];
  return <main className="min-h-screen w-full bg-paper px-6 py-8 text-text md:px-10 md:py-12" data-id="element-528" data-mp="ba1ru09i2xsg">
      <div className="mx-auto max-w-4xl" data-id="element-529" data-mp="g2lgptdn1fck">
        <a href="/" className="inline-flex items-center gap-2 rounded-xl px-2 py-2 text-sm font-semibold text-navy transition-colors hover:bg-paper-dark focus:outline-none focus:ring-2 focus:ring-seaglass" data-id="element-530" data-mp="g3mu44i4mdxg">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" data-id="element-531" data-mp="rukgbzoj9ljj" />
          Back to Author Leverage
        </a>

        <header className="border-b border-gray-200 pb-10 pt-10 text-center md:pb-12" data-id="element-532" data-mp="10x24wkrnuq9p">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-seaglass" data-id="element-533" data-mp="10v8sspya3dub">
            Your Reader-to-Revenue Score
          </p>
          <p className="mt-4 text-sm font-medium text-text-muted" data-id="element-534" data-mp="1anof1zglyol0">
            {result.totalScore} of 24 points
          </p>
          <h1 className="mx-auto mt-5 max-w-3xl font-serif text-4xl leading-tight text-navy md:text-6xl" data-id="element-535" data-mp="1om4k7lyto711">
            {result.band}
          </h1>
          <p className="mx-auto mt-7 max-w-2xl text-lg leading-relaxed text-text-muted" data-id="element-536" data-mp="3qdrb7nips1xi">
            {content.acknowledgment}
          </p>
          <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-text-muted" data-id="element-537" data-mp="3lg7xqptubt6m">
            {content.meaning}
          </p>
        </header>

        <section className="grid gap-5 py-10 text-left md:grid-cols-2 md:py-12" data-id="element-538" data-mp="1uav2igeerl6l">
          <article className="rounded-3xl border border-gray-200 bg-white p-7 md:p-8" data-id="element-539" data-mp="1ucz4gssqya48">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-seaglass" data-id="element-540" data-mp="1z8o7x77w0b5t">
              Biggest opportunity
            </p>
            <h2 className="mt-3 font-serif text-3xl text-navy" data-id="element-541" data-mp="28b5slh7w8vqp">
              {DIMENSION_LABEL[result.biggestOpportunity]}
            </h2>
            <p className="mt-4 leading-relaxed text-text-muted" data-id="element-542" data-mp="2d85xvpwx5ugr">
              {content.opportunity}
            </p>
          </article>

          <article className="rounded-3xl border border-gray-200 bg-white p-7 md:p-8" data-id="element-543" data-mp="2mzk4sir79wjx">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-seaglass" data-id="element-544" data-mp="2n1mv73w9lrp6">
              Strongest layer
            </p>
            <h2 className="mt-3 font-serif text-3xl text-navy" data-id="element-545" data-mp="2vzoxdmrqiqe1">
              {DIMENSION_LABEL[result.strongestLayer]}
            </h2>
            <p className="mt-4 leading-relaxed text-text-muted" data-id="element-546" data-mp="keg2dx1xi1u0">
              {strongestLayerExplanation(result.strongestLayer, result.dimensions[result.strongestLayer])}
            </p>
          </article>
        </section>

        <section className="rounded-3xl border border-gray-200 bg-white px-7 py-8 text-left md:px-8" aria-labelledby="next-step-heading" data-id="element-547" data-mp="be1hgysvmor1">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-seaglass" data-id="element-548" data-mp="133mev2c4px7d">
            Suggested next focus
          </p>
          <h2 id="next-step-heading" className="mt-3 font-serif text-2xl text-navy md:text-3xl" data-id="element-549" data-mp="z2av3dfxs673">
            {content.nextStep}
          </h2>
        </section>

        <section className="border-t border-gray-200 py-10 text-left md:py-12" aria-labelledby="dimensions-heading" data-id="element-550" data-mp="pcrdsc7vgkec">
          <p className="mb-4 text-center text-xs font-semibold uppercase tracking-[0.18em] text-seaglass" data-id="element-551" data-mp="1ms59hzfwm2tg">
            Your four layers
          </p>
          <h2 id="dimensions-heading" className="sr-only" data-id="element-552" data-mp="11l4afvj892l5">
            Your four score dimensions
          </h2>
          <div className="grid gap-3 sm:grid-cols-2" data-id="element-553" data-mp="16jz4xuqk4fl3">
            {DIMENSION_ORDER.map(dimension => <article key={dimension} className="rounded-2xl border border-gray-200 bg-white px-5 py-4" data-id="element-554" data-mp="skfx731nr69b">
                <div className="flex items-baseline justify-between gap-4" data-id="element-555" data-mp="xj99wt94et8f">
                  <h3 className="font-semibold text-navy" data-id="element-556" data-mp="xldoq68k5cnh">
                    {DIMENSION_LABEL[dimension]}
                  </h3>
                  <p className="text-sm font-medium text-text-muted" data-id="element-557" data-mp="1pb8do1si70bm">
                    {result.dimensions[dimension]} / 6
                  </p>
                </div>
                <p className="mt-1 text-sm text-text-muted" data-id="element-558" data-mp="1bhhdvl4kqut3">
                  {dimensionLevel(result.dimensions[dimension])}
                </p>
              </article>)}
          </div>
        </section>

        <AuditRequestForm score={result} helperText={content.ctaHelper} data-id="element-559" data-mp="2464qytf66dvk" />
      </div>
    </main>;
}
function EmptyScoreState() {
  return <main className="flex min-h-screen w-full items-center justify-center bg-paper px-6 py-12 text-text" data-id="element-560" data-mp="2icztkhzfvevx">
      <section className="w-full max-w-xl rounded-3xl border border-gray-200 bg-white p-8 text-center shadow-sm md:p-12" aria-labelledby="empty-score-heading" data-id="element-561" data-mp="2ecpkuxk9iadd">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-seaglass" data-id="element-562" data-mp="2s55jzna1lo90">
          Reader-to-Revenue Score
        </p>
        <h1 id="empty-score-heading" className="mt-4 font-serif text-4xl leading-tight text-navy" data-id="element-563" data-mp="1zdvk583zjj3q">
          This score link is incomplete or invalid.
        </h1>
        <p className="mt-5 leading-relaxed text-text-muted" data-id="element-564" data-mp="1vfenjii6xr0k">
          Complete the eight-question check to generate a fresh score page.
        </p>
        <a href="/" className="mt-8 inline-flex min-h-[48px] items-center justify-center rounded-xl bg-sand px-6 py-3 font-bold text-navy transition-colors hover:bg-sand-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-seaglass" data-id="element-565" data-mp="24cowgx7jd42b">
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" data-id="element-566" data-mp="3fv9ijg8dixt5" />
          Return to Author Leverage
        </a>
      </section>
    </main>;
}